import React, { useState } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { FloatLabel } from "primereact/floatlabel";
import { useFormik } from "formik";
import * as Yup from "yup";
import axiosInstance from "../utils/axios";
import { useAuth } from "../hooks/useAuth";
import toast from "react-hot-toast";
import { renderFieldLabel } from "../utils/formHelpers";

export const Login = () => {
  const [isSignIn, setIsSignIn] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showSignUpPassword, setShowSignUpPassword] = useState(false);
  const [showSignUpConfirm, setShowSignUpConfirm] = useState(false);
  const { login, loginLoading } = useAuth();

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string().email("Invalid email address").required("Required"),
      password: Yup.string().required("Required"),
    }),
    onSubmit: async (values) => {
      login(values, {
        onSuccess: () => {
          formik.resetForm();
        },
      });
    },
  });

  const signUpFormik = useFormik({
    initialValues: {
      username: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      username: Yup.string().required("Required"),
      email: Yup.string().email("Invalid email address").required("Required"),
      password: Yup.string()
        .min(8, "Password must be at least 8 characters")
        .matches(/[A-Z]/, "Must include at least one uppercase letter")
        .matches(/[a-z]/, "Must include at least one lowercase letter")
        .matches(/[0-9]/, "Must include at least one number")
        .matches(/[!@#$%^&*]/, "Must include at least one special character")
        .required("Required"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password"), null], "Passwords must match")
        .required("Confirm Password is required"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      await axiosInstance
        .post(`/api/auth/register`, {
          username: values.username,
          email: values.email,
          password: values.password,
        })
        .then((res) => {
          toast.success(res.data.message);
          setTimeout(() => {
            setLoading(false);
            signUpFormik.resetForm();
            formik.resetForm();
            setIsSignIn(true);
          }, 2000);
        })
        .catch((err) => {
          setLoading(false);
          toast.error(err.response?.data?.message || "Registration failed");
        });
    },
  });

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f8f9fa] to-[#e9ecef]">
      <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-8">
        <h2 className="text-2xl text-center mb-6 text-[#BA1500]">
          {isSignIn ? "Log In" : "Sign Up"}
        </h2>
        {isSignIn ? (
          <form onSubmit={formik.handleSubmit} className="space-y-5">
            <div>
              <FloatLabel>
                <InputText
                  id="email"
                  name="email"
                  type="email"
                  className="w-full border border-gray-300 p-2 rounded-md placeholder:text-sm focus:outline-none focus:ring-2 focus:ring-[#BA1500]"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                  autoComplete="email"
                />
                <label htmlFor="email" className="text-sm">
                  Email <span className="text-red-500 ml-1">*</span>
                </label>
              </FloatLabel>
              {formik.touched.email && formik.errors.email && (
                <div className="text-[#BA1500] text-xs mt-1">
                  {formik.errors.email}
                </div>
              )}
            </div>
            <div>
              <FloatLabel>
                <span className="relative w-full flex">
                  <InputText
                    id="password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    className="w-full border border-gray-300 p-2 rounded-md placeholder:text-sm focus:outline-none focus:ring-2 focus:ring-[#BA1500] pr-10"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.password}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
                    onClick={() => setShowPassword((prev) => !prev)}
                  >
                    <i
                      className={`pi ${
                        showPassword ? "pi-eye-slash" : "pi-eye"
                      }`}
                    ></i>
                  </button>
                </span>
                <label htmlFor="password" className="text-sm">
                  Password <span className="text-red-500 ml-1">*</span>
                </label>
              </FloatLabel>
              {formik.touched.password && formik.errors.password && (
                <div className="text-[#BA1500] text-xs mt-1">
                  {formik.errors.password}
                </div>
              )}
            </div>
            <div>
              {loginLoading ? (
                <div className="w-full flex justify-center items-center py-2 bg-[#BA1500] rounded-md">
                  <i
                    className="pi pi-spin pi-spinner text-white"
                    style={{ fontSize: "2rem" }}
                  ></i>
                </div>
              ) : (
                <Button
                  label="Log In"
                  type="submit"
                  className="w-full bg-[#BA1500] text-center text-white py-3"
                />
              )}
            </div>
            <div className="w-full text-center py-2">
              <p>
                Don't have an account?{" "}
                <button
                  type="button"
                  onClick={() => setIsSignIn(false)}
                  className="text-red-600 text-sm underline"
                >
                  Create an account
                </button>
              </p>
            </div>
          </form>
        ) : (
          <form onSubmit={signUpFormik.handleSubmit} className="space-y-5">
            <div>
              <FloatLabel>
                <InputText
                  id="username"
                  name="username"
                  className="w-full border border-gray-300 p-2 rounded-md placeholder:text-sm focus:outline-none focus:ring-2 focus:ring-[#BA1500]"
                  onChange={signUpFormik.handleChange}
                  onBlur={signUpFormik.handleBlur}
                  value={signUpFormik.values.username}
                  autoComplete="username"
                />
                <label htmlFor="username" className="text-sm">
                  Username <span className="text-red-500 ml-1">*</span>
                </label>
              </FloatLabel>
              {signUpFormik.touched.username &&
                signUpFormik.errors.username && (
                  <div className="text-[#BA1500] text-xs mt-1">
                    {signUpFormik.errors.username}
                  </div>
                )}
            </div>
            <div>
              <FloatLabel>
                <InputText
                  id="email"
                  name="email"
                  type="email"
                  className="w-full border border-gray-300 p-2 rounded-md placeholder:text-sm focus:outline-none focus:ring-2 focus:ring-[#BA1500]"
                  onChange={signUpFormik.handleChange}
                  onBlur={signUpFormik.handleBlur}
                  value={signUpFormik.values.email}
                  autoComplete="email"
                />
                <label htmlFor="email" className="text-sm">
                  Email <span className="text-red-500 ml-1">*</span>
                </label>
              </FloatLabel>
              {signUpFormik.touched.email && signUpFormik.errors.email && (
                <div className="text-[#BA1500] text-xs mt-1">
                  {signUpFormik.errors.email}
                </div>
              )}
            </div>
            <div>
              <FloatLabel>
                <span className="relative w-full flex">
                  <InputText
                    id="password"
                    name="password"
                    type={showSignUpPassword ? "text" : "password"}
                    className="w-full border border-gray-300 p-2 rounded-md placeholder:text-sm focus:outline-none focus:ring-2 focus:ring-[#BA1500] pr-10"
                    onChange={signUpFormik.handleChange}
                    onBlur={signUpFormik.handleBlur}
                    value={signUpFormik.values.password}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
                    onClick={() => setShowSignUpPassword((prev) => !prev)}
                  >
                    <i
                      className={`pi ${
                        showSignUpPassword ? "pi-eye-slash" : "pi-eye"
                      }`}
                    ></i>
                  </button>
                </span>
                <label htmlFor="password" className="text-sm">
                  Password <span className="text-red-500 ml-1">*</span>
                </label>
              </FloatLabel>
              {signUpFormik.touched.password &&
                signUpFormik.errors.password && (
                  <div className="text-[#BA1500] text-xs mt-1">
                    {signUpFormik.errors.password}
                  </div>
                )}
            </div>
            <div>
              <FloatLabel>
                <span className="relative w-full flex">
                  <InputText
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showSignUpConfirm ? "text" : "password"}
                    className="w-full border border-gray-300 p-2 rounded-md placeholder:text-sm focus:outline-none focus:ring-2 focus:ring-[#BA1500] pr-10"
                    onChange={signUpFormik.handleChange}
                    onBlur={signUpFormik.handleBlur}
                    value={signUpFormik.values.confirmPassword}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
                    onClick={() => setShowSignUpConfirm((prev) => !prev)}
                  >
                    <i
                      className={`pi ${
                        showSignUpConfirm ? "pi-eye-slash" : "pi-eye"
                      }`}
                    ></i>
                  </button>
                </span>
                <label htmlFor="confirmPassword" className="text-sm">
                  Confirm password <span className="text-red-500 ml-1">*</span>
                </label>
              </FloatLabel>
              {signUpFormik.touched.confirmPassword &&
                signUpFormik.errors.confirmPassword && (
                  <div className="text-[#BA1500] text-xs mt-1">
                    {signUpFormik.errors.confirmPassword}
                  </div>
                )}
            </div>
            <div>
              {loading ? (
                <div className="w-full flex justify-center items-center py-2 bg-[#BA1500] rounded-md">
                  <i
                    className="pi pi-spin pi-spinner text-white"
                    style={{ fontSize: "2rem" }}
                  ></i>
                </div>
              ) : (
                <Button
                  label="Sign Up"
                  type="submit"
                  className="w-full bg-[#BA1500] text-center text-white py-3"
                />
              )}
            </div>
            <div className="w-full text-center py-2">
              <p>
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => setIsSignIn(true)}
                  className="text-red-600 text-sm underline"
                >
                  Login
                </button>
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
